import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPendingChefs,
  getApprovedChefs,
  approveChef,
} from "../../services/chef.service";
import { Spinner } from "../../components/common/Spinner";
import { Button } from "../../components/common/Button";
import { Card } from "../../components/common/Card";
import { toast } from "react-hot-toast";

type ChefSectionProps = {
  trigger?: number;
  onAdminEvent?: () => void;
};

export const ChefSection: React.FC<ChefSectionProps> = ({
  trigger,
  onAdminEvent,
}) => {
  const queryClient = useQueryClient();
  const [showPending, setShowPending] = useState(false);

  const {
  data: pendingChefs = [],
  isLoading: isPendingLoading,
} = useQuery(["pendingChefs", trigger], getPendingChefs);

const {
  data: approvedChefs = [],
  isLoading: isApprovedLoading,
} = useQuery(["approvedChefs", trigger], getApprovedChefs);


  const { mutate: approveChefMutate, isLoading: isApproving } = useMutation(
    approveChef,
    {
      onSuccess: () => {
        toast.success("Chef approved!");
        queryClient.invalidateQueries(["pendingChefs"]);
        queryClient.invalidateQueries(["approvedChefs"]);
        onAdminEvent && onAdminEvent();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Failed to approve chef.");
      },
    }
  );

  const handleApprove = (chefId: string) => {
    if (window.confirm("Are you sure you want to approve this chef?")) {
      approveChefMutate(chefId);
    }
  };

  const isLoading = isPendingLoading || isApprovedLoading;
  const data = showPending ? pendingChefs : approvedChefs;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Manage Chefs</h2>
      </div>

      <div className="flex gap-2 mb-4">
        
        <Button
          variant={!showPending ? "primary" : "secondary"}
          onClick={() => setShowPending(false)}
        >
          Approved Chefs ({approvedChefs.length})
        </Button>
		<Button
          variant={showPending ? "primary" : "secondary"}
          onClick={() => setShowPending(true)}
        >
          Pending Approval ({pendingChefs.length})
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-left text-sm">
              <thead>
                <tr className="bg-gray-100 text-gray-600">
                  <th className="p-3">Name</th>
                  <th className="p-3">Phone</th>
                  <th className="p-3">Email</th>
                  <th className="p-3">Cuisines</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.map((chef: any) => (
                  <tr key={chef.uid} className="hover:bg-gray-50">
                    <td className="p-3 font-medium">
                      {chef.displayName || chef.name}
                    </td>
                    <td className="p-3">{chef.phone}</td>
                    <td className="p-3">{chef.email}</td>
                    <td className="p-3 text-gray-500">
                      {chef.cuisines?.join(", ") || "N/A"}
                    </td>
                    <td className="p-3">
                      {showPending ? (
                        <Button
                          variant="primary"
                          className="py-1 px-3 text-xs"
                          onClick={() => handleApprove(chef.uid)}
                          isLoading={isApproving}
                        >
                          Approve
                        </Button>
                      ) : (
                        <span className="text-green-600 font-semibold text-xs border border-green-200 bg-green-50 px-2 py-1 rounded">
                          Approved
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="p-6 text-center text-gray-500 text-sm"
                    >
                      No {showPending ? "pending" : "approved"} chefs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
